import { userService } from "../services";
import { VehicleAction } from "./vehicle"
import config from "../config/config";;

const FALLBACK_FMS_URL = "http://192.168.60.198:8000/";

const getBaseUrl = () => {
  try {
    const config = JSON.parse(localStorage.getItem("config") || "{}");
    const datas =
      typeof config?.datas === "string" ? JSON.parse(config.datas) : config?.datas;
    const url = datas?.fmsbaseUrl || datas?.fmsBaseUrl;
    if (url) return url.endsWith("/") ? url : `${url}/`;
  } catch (e) {
    console.error("getBaseUrl: could not read fmsbaseUrl from config", e);
  }
  return FALLBACK_FMS_URL;
};

const STATUS_TITLE_CASE = {
  STOPPED: "Stopped",
  IDLE: "Idle",
  RUNNING: "Running",
  OFFLINE: "Offline",
};

function formatEngineHours(ms) {
  if (ms == null) return "—";
  const totalMinutes = Math.floor(Number(ms) / 60000);
  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hrs} Hr ${mins} Min`;
}

function formatLastUpdated(ts) {
  if (!ts) return "";
  return `Last Updated: ${new Date(ts).toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

const buildExportQs = ({ fromDate, toDate, siteIds, vehicleTypeIds, vehicleIds, status, search, isFuelWirecut } = {}) => {
  const qs = new URLSearchParams();
  qs.append("fromDate", fromDate);
  qs.append("toDate", toDate);
  toArray(siteIds).forEach((id) => qs.append("siteIds", id));
  toArray(vehicleTypeIds).forEach((id) => qs.append("vehicleTypeIds", id));
  toArray(vehicleIds).forEach((id) => qs.append("vehicleIds", id));
  if (status) qs.append("status", status);
  if (search) qs.append("search", search);
  if (typeof isFuelWirecut === "boolean") qs.append("isFuelWirecut", isFuelWirecut);
  return qs.toString();
};

function mapFuelDashboardResponse(raw) {
  const s = raw?.summary || {};
  const page = raw?.vehicles || {};
  const items = Array.isArray(page.content) ? page.content : [];

  const rows = items.map((v) => {
    const status = STATUS_TITLE_CASE[v.status] || "Offline";
    const noSensor = v.currentFuelL == null; // sensorOk is no longer in the response

    const tankCapacity = v.tankCapacityL;
    const engineHours = v.engineHours
    const currentFuelL = noSensor ? null : Number(v.currentFuelL);
    const fuelPct =
      tankCapacity && currentFuelL != null
        ? Math.min(100, Math.max(0, (currentFuelL / tankCapacity) * 100))
        : null;

    return {
      id: v.deviceId ?? v.regNo,
      vehicleNo: v.regNo,
      subtitle: v.vehicleType || "",
      site: v.site || "",
      driver: v.driverName ?? "NA",
      driverPhone: v.driverPhone ?? "",
      status,
      tankCapacity,
      currentFuelL,
      currentFuel: noSensor ? "No sensor" : `${currentFuelL.toFixed()} L`,
      fuelPct,
      currentFuelColor: status === "Idle" ? "#d97706" : undefined,

      runningDistance: !v.fmsInstalled
        ? "FMS not installed"
        : v.distanceKm == null
          ? "—"
          : {
            startKm: v.startKm != null ? Number(v.startKm).toFixed(2) : "—",
            endKm: v.endKm != null ? Number(v.endKm).toFixed(2) : "—",
            distanceKm: `${Number(v.distanceKm).toFixed(2)} km`,
            // not in the new response → null, hidden by the render below
            engineHours: v.engineHours != null ? engineHours : null,
            startFuel: v.startFuelL != null ? `${Number(v.startFuelL).toFixed(2)} L` : null,
            endFuel: v.endFuelL != null ? `${Number(v.endFuelL).toFixed(2)} L` : null,
          },

      consumption: !v.fmsInstalled
        ? "FMS not installed"
        : v.expectedL == null
          ? "no trip yet"
          : {
            expected: `${Number(v.expectedL).toFixed(2)} L`,
            actual: `${Number(v.actualL ?? 0).toFixed(2)} L`,
            dev: `${Number(v.deviationL ?? 0).toFixed(2)} L`,
            devDir: v.deviationDirection === "DOWN" ? "down" : "up",
            expectedKmpl: v.expectedKmpl != null ? Number(v.expectedKmpl).toFixed(2) : "—",
            actualKmpl: v.runningKmpl != null ? Number(v.runningKmpl).toFixed(2) : "—",
          },

      droppage: !v.fmsInstalled
        ? "FMS not installed"
        : v.confirmedDropL == null
          ? "—"
          : {
            confirmed: `${Number(v.confirmedDropL).toFixed(2)} L`,
            unchecked: `${Number(v.uncheckedDropL ?? 0).toFixed(2)} L`,
          },

      fill: v.fmsInstalled ? `${Number(v.fillL ?? 0).toFixed(2)} L` : "—",
      lastUpdated: formatLastUpdated(v.lastUpdated), // was lastUpdateTs
      disabled: !v.fmsInstalled,
    };
  });

  return {
    // remapped to the keys your StatCards already read, so the JSX doesn't change
    stats: {
      totalWireCut: s.totalWireCut,
      confirmedDroppage: s.confirmedDroppageL,
      uncheckedDroppage: s.uncheckedDroppageL,
      expectedConsumption: s.expectedConsumptionL,
      actualConsumption: s.actualConsumptionL,
      totalDeviation: s.totalDeviationL,
      totalFuelFill: s.totalFuelFillL,
    },
    vehicleStatusList: rows,
    totalElements: page.totalElements ?? rows.length,
  };
}

export const FmsDashboardAction = {
  getFmsDashboardReport,
  getFmsSiteDropdown,
  getFmsVehicleTypeDropdown,
  getFmsVehicleDropdown,
  exportFmsExcel,
  exportFmsDayWiseExcel,
  exportFmsPdf,
  getFmsFuelGraph
};

const toArray = (v) => (v == null || v === "" ? [] : Array.isArray(v) ? v : [v]);

// fromDate / toDate must already be "DD/MM/YYYY HH:mm"
async function getFmsDashboardReport({
  fromDate,
  toDate,
  siteIds,          // single value or array
  vehicleTypeIds,   // single value or array
  vehicleIds,       // single value or array
  status = null,    // RUNNING | IDLE | STOP | OFFLINE
  search = "",
  isFuelWirecut,    // true | false | undefined
  pageNo = 0,
  pageSize = 50,    // API max 200
} = {}) {
  try {
    const qs = new URLSearchParams();
    qs.append("fromDate", fromDate);
    qs.append("toDate", toDate);
    toArray(siteIds).forEach((id) => qs.append("siteIds", id));
    toArray(vehicleTypeIds).forEach((id) => qs.append("vehicleTypeIds", id));
    toArray(vehicleIds).forEach((id) => qs.append("vehicleIds", id));
    if (status) qs.append("status", status);
    if (search) qs.append("search", search);
    if (typeof isFuelWirecut === "boolean") qs.append("isFuelWirecut", isFuelWirecut);
    qs.append("page", pageNo);
    qs.append("size", pageSize);

    const apiEndpoint = `${getBaseUrl()}${config.apiName.FmsDashboardReport}?${qs.toString()}`;
    const response = await userService.get(apiEndpoint);
    if (response && response.data) return mapFuelDashboardResponse(response.data);
    return null;
  } catch (error) {
    console.log("getFmsDashboardReport error", error);
    return null;
  }
}

async function getFmsSiteDropdown() {
  try {
    const apiEndpoint = `${getBaseUrl()}${config.apiName.FmsSiteList}`;
    const response = await userService.get(apiEndpoint);
    if (response && response.data) return response.data;
    return [];
  } catch (error) {
    console.log("getFmsSiteDropdown error", error);
    return [];
  }
}

async function getFmsVehicleTypeDropdown() {
  try {
    const res = await VehicleAction.vehicleTypeList();
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  } catch (error) {
    console.log("getFmsVehicleTypeDropdown error", error);
    return [];
  }
}

async function getFmsVehicleDropdown(siteId = null) {
  const localData = JSON.parse(localStorage.getItem("data") || "{}");
  try {
    const res = await VehicleAction.fetchVehicleListV6({
      pageNo: 0,
      pageSize: 0,
      companyId: localData?.companyId,
      number: "",
      vehicleType: "",
      driver: "",
      siteId: siteId || "",
      siteIds: siteId ? [siteId] : undefined,
      userId: localData?.id,
      onlyGpsVehicle: true,
      forDropDown: "true",
    });

    // API returns a plain array of vehicles directly
    if (Array.isArray(res)) return res;
    // fallback in case it's ever wrapped as { data: [...] }
    if (Array.isArray(res?.data)) return res.data;
    return [];
  } catch (error) {
    console.log("getFmsVehicleDropdown error", error);
    return [];
  }
}

// fetch + res.blob() keeps the raw bytes intact (no text decoding)
async function downloadBlob(endpointKey, params) {
  try {
    const url = `${getBaseUrl()}${config.apiName[endpointKey]}?${buildExportQs(params)}`;
    const token = JSON.parse(localStorage.getItem("data"))?.token;

    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    return await res.blob();
  } catch (error) {
    console.log(`${endpointKey} error`, error);
    return null;
  }
}

async function exportFmsExcel(params) {
  return downloadBlob("FmsExportExcel", params);
}

async function exportFmsDayWiseExcel(params) {
  return downloadBlob("FmsExportDayWiseExcel", params);
}

async function exportFmsPdf(params) {
  return downloadBlob("FmsExportPdf", params);
}

const toMs = (v) =>
  v == null ? null : typeof v === "number" ? v : Date.parse(v);

function normalizeFuelGraph(raw) {
  if (!raw) return null;

  const points = (raw.points || []).map((p) => ({
    ...p,
    fuelL: p.fuelL ?? p.fuel,
    lon: p.lon ?? p.lng,
    tilt: p.tilt ?? p.titl ?? 0,
  }));

  const src = raw.detectedEvents?.length ? raw.detectedEvents : raw.markers || [];

  const markers = src
    .map((m) => {
      const ts = toMs(m.ts ?? m.time ?? m.start);
      return {
        id: m.id,
        type: String(m.type || "").toUpperCase(),
        status: m.status,
        ts,
        endTs: toMs(m.end) ?? ts,
        litres: Math.abs(Number(m.litres ?? m.deltaL ?? m.volumeL ?? 0)),
        beforeL: m.beforeL,
        afterL: m.afterL,
        positionId: m.positionId ?? m.postionId,
      };
    })
    .filter((m) => m.ts != null && m.litres > 0);

  const s = raw.summary || {};
  return { ...raw, points, markers, summary: { ...s, positionId: s.positionId ?? s.postionId } };
}

async function getFmsFuelGraph({ vehicleId, fromDate, toDate } = {}) {
  if (!vehicleId || !fromDate || !toDate) return null;
  try {
    const qs = new URLSearchParams({ fromDate, toDate }).toString();
    const apiEndpoint = `${getBaseUrl()}${config.apiName.FmsFuelGraphBase}?deviceId=${vehicleId}&${qs}`;
    const response = await userService.get(apiEndpoint);
    if (response && response.data) return normalizeFuelGraph(response.data);
    return null;
  } catch (error) {
    console.log("getFmsFuelGraph error", error);
    return null;
  }
}
